"use client";

import { useMediaQuery } from "@/app/(site)/_hooks/useMediaQuery";
import { AboutPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/lib/image";
import Image from "next/image";
import { motion, useAnimate, stagger } from "framer-motion";
import { useState } from "react";
import clsx from "clsx";

type TeamMembersDesktopProps = {
	team: NonNullable<AboutPageQueryResult>["ourTeam"]["teamMembers"];
};

export default function TeamMembersDesktop({ team }: TeamMembersDesktopProps) {
	const isDesktop = useMediaQuery("(min-width: 990px)");
	const [hoveredMember, setHoveredMember] = useState<string | null>(null);
	const [scope, animate] = useAnimate();

	const handleMouseEnter = (memberId: string) => {
		setHoveredMember(memberId);

		// Animate the position text with typewriter effect
		animate(
			`#position-${memberId} span`,
			{
				opacity: 1,
				x: 0,
			},
			{
				duration: 0.05,
				delay: stagger(0.05),
			},
		);
	};

	const handleMouseLeave = (memberId: string) => {
		setHoveredMember(null);

		// Reset position text animation
		animate(
			`#position-${memberId} span`,
			{
				opacity: 0,
				x: -10,
			},
			{
				duration: 0.01,
			},
		);
	};

	if (isDesktop) {
		const currentImage = team.find((member) => member._key === hoveredMember) || team[0];

		return (
			<div className="col-span-full">
				<div ref={scope} className="grid grid-cols-12 gap-x-8">
					<div className="col-span-9">
						{team.map((member) => {
							const isHovered = hoveredMember === member._key;
							const positionLetters = member.position.split("");

							return (
								<div
									key={member._key}
									className="border-graphite-black/10 border-t py-8"
									onMouseEnter={() => handleMouseEnter(member._key)}
									onMouseLeave={() => handleMouseLeave(member._key)}
								>
									<div className="grid grid-cols-9">
										<div className="col-span-5">
											<motion.p
												className={clsx(
													"text-body-xxl transition-colors duration-300",
													isHovered ? "text-brown-middle" : "text-graphite-black",
												)}
											>
												{member.name}
											</motion.p>
										</div>
										<div className="col-span-4 self-center">
											<div
												id={`position-${member._key}`}
												className="text-cap-m text-graphite-black/70 overflow-hidden"
											>
												{isHovered ? (
													// Typewriter effect only on hover
													positionLetters.map((letter, index) => (
														<motion.span
															key={index}
															initial={{ opacity: 0, x: -10 }}
															animate={{ opacity: 1, x: 0 }}
															transition={{
																duration: 0.05,
																delay: index * 0.05,
															}}
															className="inline-block"
														>
															{letter === " " ? "\u00A0" : letter}
														</motion.span>
													))
												) : (
													<span>{member.position}</span>
												)}
											</div>
										</div>
									</div>
								</div>
							);
						})}
					</div>

					<div className="col-span-3">
						<div className="sticky top-32">
							{currentImage && (
								<motion.div
									key={currentImage._key}
									className="relative aspect-[331/414] overflow-hidden rounded-lg"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									transition={{ duration: 0.3 }}
								>
									<Image
										src={urlFor(currentImage.image).url()}
										alt={currentImage.name}
										fill
										className="object-cover"
										sizes="33vw"
									/>
									<div className="bg-graphite-black/20 absolute inset-0" />
								</motion.div>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}
}
